import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Mail } from "lucide-react";

const BRANCHES = [
  {
    city: "Gurugram",
    region: "National Capital Region",
    image:
      "https://images.unsplash.com/photo-1688139885160-bb49f176ae2e?w=1600&q=85&auto=format&fit=crop",
    blurb:
      "Our NCR office serves clients across financial services, automotive, and large-cap industrials — the engine room of India's enterprise SAP demand.",
    industries: ["Financial Services", "Automotive", "Industrials"],
  },
  {
    city: "Mumbai",
    region: "Maharashtra",
    image:
      "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=1600&q=85&auto=format&fit=crop",
    blurb:
      "From India's commercial capital, our Mumbai practice supports BFSI, FMCG, retail and pharma clients with strategic SAP and analytics engagements.",
    industries: ["BFSI", "FMCG & Retail", "Pharma"],
  },
  {
    city: "Bangalore",
    region: "Karnataka",
    image:
      "https://images.unsplash.com/photo-1588416936097-41850ab3d86d?w=1600&q=85&auto=format&fit=crop",
    blurb:
      "Anchored in India's tech capital, our Bangalore team partners with technology, manufacturing and consumer firms on cloud, RISE with SAP and digital transformation.",
    industries: ["Technology", "Manufacturing", "Consumer"],
  },
];

const Branches = () => {
  return (
    <div data-testid="branches-page">
      {/* HERO */}
      <section
        data-testid="branches-hero"
        className="bg-[#F5F5DC] pt-32 pb-24 lg:pt-40 lg:pb-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="overline mb-6 fade-up">Our Branches</p>
            <h1 className="font-display text-5xl lg:text-7xl text-[#111111] leading-[1.05] fade-up-delay-1">
              A national footprint.
              <br />
              <span className="text-[#8B0000]">A senior-led team.</span>
            </h1>
            <p className="mt-6 font-display italic text-2xl text-[#8B0000] fade-up-delay-2">
              Empowering Business. Accelerating Growth.
            </p>
          </div>
          <div className="lg:col-span-5 flex items-end fade-up-delay-2">
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Anila Consulting operates across India's three most active
              enterprise hubs — Gurugram, Mumbai and Bangalore — bringing
              local presence and deep SAP expertise wherever our clients
              build, scale, and transform.
            </p>
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section data-testid="branches-grid" className="section bg-white">
        <div className="container-pro">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {BRANCHES.map((branch, i) => (
              <article
                key={branch.city}
                data-testid={`branch-card-${branch.city}`}
                className="group bg-white border border-[#E5E5E5] hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 fade-up flex flex-col"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#111111]">
                  <img
                    src={branch.image}
                    alt={`${branch.city} cityscape`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-white/85 mb-1">
                        {branch.region}
                      </p>
                      <h3 className="font-display text-4xl text-white leading-none">
                        {branch.city}
                      </h3>
                    </div>
                    <span className="overline text-[10px] tracking-[0.25em] text-white/85 bg-[#8B0000] px-3 py-1.5">
                      {`0${i + 1}`}
                    </span>
                  </div>
                </div>

                <div className="p-8 lg:p-10 flex-1 flex flex-col">
                  <p className="text-[#4A4A4A] leading-relaxed flex-1">
                    {branch.blurb}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRESENCE STATEMENT */}
      <section
        data-testid="branches-presence-section"
        className="section bg-[#F5F5DC]"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-5">Local presence, national reach</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              Wherever your business
              <br />
              <span className="text-[#8B0000]">scales next — we're already there.</span>
            </h2>
            <p className="text-[#4A4A4A] leading-relaxed mt-6 text-lg">
              From the corporate parks of Gurugram to the financial corridors
              of Mumbai and the technology campuses of Bangalore, our senior
              consultants engage on the ground — meeting your stakeholders,
              auditing your systems, and embedding with your teams as long as
              the engagement requires.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div
              data-testid="branches-contact-card"
              className="bg-white p-8 lg:p-10 border border-[#E5E5E5]"
            >
              <p className="overline mb-4">Single point of contact</p>
              <h3 className="font-display text-2xl text-[#111111] mb-4 leading-snug">
                One inbox. Three offices. National coverage.
              </h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-6">
                Reach our central team and we'll route your query to the
                nearest practice within one business day.
              </p>
              <a
                href="mailto:anila@anilaconsulting.com"
                data-testid="branches-email-link"
                className="inline-flex items-center gap-3 text-[#8B0000] font-medium uppercase tracking-wider text-sm hover:gap-4 transition-all"
              >
                <Mail size={16} /> anila@anilaconsulting.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="branches-cta-section"
        className="bg-[#8B0000] text-white py-24 lg:py-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-6xl leading-tight text-white">
              Schedule a meeting
              <br />
              at your nearest office.
            </h2>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              Tell us where you are and what you're working on. A senior
              consultant from the closest branch will reach out personally
              within one business day.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="branches-cta-button"
              className="btn-ghost-light"
            >
              Get in Touch <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Branches;
